import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const API_URL = '/api/merchant-pga';

function MerchantPGATab() {
  const { authFetch } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ type: 'B2C', tier: 1, pga_name: '', pga_id_ext: '', is_active: true });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(API_URL);
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error('Gagal mengambil data Merchant PGA');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFormChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : (name === 'tier' || name === 'pga_id_ext') ? (value === '' ? '' : parseInt(value)) : value
    }));
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ type: 'B2C', tier: 1, pga_name: '', pga_id_ext: '', is_active: true });
    setShowForm(true);
  };

  const handleEdit = (record) => {
    setEditing(record.id);
    setForm({ type: record.type, tier: record.tier, pga_name: record.pga_name, pga_id_ext: record.pga_id_ext, is_active: record.is_active });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.pga_name || !form.pga_id_ext) {
      toast.error('PGA Name dan PGA ID harus diisi');
      return;
    }
    try {
      const url = editing ? `${API_URL}/${editing}` : API_URL;
      const method = editing ? 'PUT' : 'POST';
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Gagal menyimpan');
      }
      toast.success(editing ? 'PGA berhasil diupdate' : 'PGA berhasil ditambahkan');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus PGA ini?')) return;
    try {
      const response = await authFetch(`${API_URL}/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Gagal menghapus');
      toast.success('PGA berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggle = async (record) => {
    try {
      const response = await authFetch(`${API_URL}/${record.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !record.is_active })
      });
      if (!response.ok) throw new Error('Gagal update status');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filtered = data.filter(item =>
    item.pga_name.toLowerCase().includes(search.toLowerCase()) ||
    item.type.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="tab-header-actions">
        <h2>Merchant PGA Configuration</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search PGA..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button onClick={handleAdd} className="add-new-btn">Add PGA</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginTop: 0 }}>{editing ? 'Edit PGA' : 'Add PGA'}</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Type</label>
              <select name="type" value={form.type} onChange={handleFormChange} style={{ padding: '6px 10px' }}>
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Tier</label>
              <input type="number" name="tier" value={form.tier} onChange={handleFormChange} min="1" style={{ padding: '6px 10px', width: '60px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>PGA Name</label>
              <input type="text" name="pga_name" value={form.pga_name} onChange={handleFormChange} placeholder="e.g. LINK1" style={{ padding: '6px 10px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>PGA ID (ext)</label>
              <input type="number" name="pga_id_ext" value={form.pga_id_ext} onChange={handleFormChange} placeholder="e.g. 52" style={{ padding: '6px 10px', width: '80px' }} />
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={handleSave} className="add-new-btn">{editing ? 'Update' : 'Save'}</button>
              <button onClick={() => setShowForm(false)} style={{ padding: '6px 12px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading data...</p>
      ) : (
        <div className="table-responsive">
          <table className="master-data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Tier</th>
                <th>PGA Name</th>
                <th>PGA ID (ext)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td><span style={{ padding: '2px 8px', borderRadius: '4px', background: item.type === 'B2B' ? '#e3f2fd' : '#fff3e0', fontWeight: '500' }}>{item.type}</span></td>
                  <td>{item.tier}</td>
                  <td style={{ fontWeight: '500' }}>{item.pga_name}</td>
                  <td>{item.pga_id_ext}</td>
                  <td>
                    <label className="switch-table">
                      <input type="checkbox" checked={item.is_active} onChange={() => handleToggle(item)} />
                      <span className="slider round"></span>
                    </label>
                  </td>
                  <td className="actions-cell">
                    <button onClick={() => handleEdit(item)}>Edit</button>
                    <button onClick={() => handleDelete(item.id)} className="delete-btn">Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No records found.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
            Total: {filtered.length} PGA ({data.filter(d => d.type === 'B2C').length} B2C, {data.filter(d => d.type === 'B2B').length} B2B)
          </div>
        </div>
      )}
    </div>
  );
}

export default MerchantPGATab;
